/**
 * React hooks for the menu-cards API.
 *
 * Backed by the four endpoints in `api/src/functions/menuCards.ts`:
 *
 *   useMenuCardServices(from, to)
 *     Lists services + last refresh + any in-flight refresh request.
 *     Refetched on date-range change. Polls when a refresh is running.
 *
 *   useMenuCardsForService(date, time, description)
 *     Fetches the booking rows for one specific service. Lazy — only
 *     fires when all three identifiers are present.
 *
 *   useTriggerMenuCardsRefresh()
 *     Returns a `trigger()` function plus a polling state. Calls POST,
 *     then polls GET /refresh-requests/{id} until completed/error.
 *
 * All hooks are no-deps — pure fetch + useState + useEffect. Matches the
 * platform's existing hook style (see useSeasonedRows / usePullmanServices
 * in App.tsx).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  MenuCardServicesResponse,
  MenuCardsForServiceResponse,
  MenuCardBookingsSearchResponse,
  RefreshMenuCardsPostResponse,
  RefreshRequestStatusResponse,
  RefreshRequestStatus,
  DietaryOverrideOp,
} from "./types";

// ---------------------------------------------------------------------------
// Tiny fetch helper — bubbles non-2xx responses with a useful message.
// ---------------------------------------------------------------------------
async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) detail = String(body.error);
      if (body?.detail) detail = `${detail} — ${body.detail}`;
    } catch {
      /* response wasn't JSON; keep statusText */
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// saveMenuCardDietaryOverrides(ops) — POST the edited dietary comments.
// Non-empty text saves an override; empty text resets that booking to MERAC.
// ---------------------------------------------------------------------------
export async function saveMenuCardDietaryOverrides(
  ops: DietaryOverrideOp[]
): Promise<{ saved: number; cleared: number }> {
  const body = await getJson<{ ok: boolean; saved: number; cleared: number }>(
    "/api/dashboards/pullman/menu-card-dietary-overrides",
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ops }) }
  );
  return { saved: Number(body.saved ?? 0), cleared: Number(body.cleared ?? 0) };
}

// ---------------------------------------------------------------------------
// useMenuCardServices(from, to)
// ---------------------------------------------------------------------------
export interface UseMenuCardServicesResult {
  data: MenuCardServicesResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMenuCardServices(
  from: string | null,
  to: string | null,
  enabled = true,
  group: string | null = null
): UseMenuCardServicesResult {
  const [data, setData] = useState<MenuCardServicesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track the latest in-flight call so an older response doesn't overwrite
  // a newer one if the user changes the range mid-fetch.
  const seqRef = useRef(0);

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (group) params.set("group", group);
    const qs = params.toString();
    return `/api/dashboards/pullman/menu-card-services${qs ? `?${qs}` : ""}`;
  }, [from, to, group]);

  const fetchOnce = useCallback(async () => {
    if (!enabled) return;
    const mySeq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const json = await getJson<MenuCardServicesResponse>(buildUrl());
      if (mySeq === seqRef.current) {
        setData(json);
      }
    } catch (err) {
      if (mySeq === seqRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mySeq === seqRef.current) {
        setLoading(false);
      }
    }
  }, [buildUrl, enabled]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return {
    data,
    loading,
    error,
    refresh: fetchOnce,
  };
}

// ---------------------------------------------------------------------------
// useMenuCardsForService(date, time, description)
// Lazy — only fires when all three are non-empty.
// ---------------------------------------------------------------------------
export interface UseMenuCardsForServiceResult {
  data: MenuCardsForServiceResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMenuCardsForService(
  date: string | null,
  time: string | null,
  description: string | null
): UseMenuCardsForServiceResult {
  const [data, setData] = useState<MenuCardsForServiceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const enabled = Boolean(date && time && description);

  const fetchOnce = useCallback(async () => {
    if (!enabled || !date || !time || !description) return;
    const mySeq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ date, time, description });
      const json = await getJson<MenuCardsForServiceResponse>(
        `/api/dashboards/pullman/menu-cards?${params.toString()}`
      );
      if (mySeq === seqRef.current) {
        setData(json);
      }
    } catch (err) {
      if (mySeq === seqRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mySeq === seqRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, date, time, description]);

  useEffect(() => {
    if (enabled) {
      void fetchOnce();
    } else {
      // Reset cleanly when the user de-selects a service.
      setData(null);
      setError(null);
    }
  }, [enabled, fetchOnce]);

  return { data, loading, error, refresh: fetchOnce };
}

// ---------------------------------------------------------------------------
// useMenuCardBookingSearch(q, from, to)
//
// Backs the "Just one card" mode in the Reports A La Carte modal. Debounces
// `q` by 250ms so live typing doesn't fire a request per keystroke. Empty
// query returns every booking in the date range (capped at the API limit).
//
// Date range defaults to today through today+60d on the server side, so
// passing nulls is fine for the default forward-looking view.
// ---------------------------------------------------------------------------
export interface UseMenuCardBookingSearchResult {
  data: MenuCardBookingsSearchResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SEARCH_DEBOUNCE_MS = 250;

export function useMenuCardBookingSearch(
  q: string,
  from: string | null,
  to: string | null,
  enabled = true,
  group: string | null = null
): UseMenuCardBookingSearchResult {
  const [data, setData] = useState<MenuCardBookingsSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debouncedQ, setDebouncedQ] = useState(q);
  const seqRef = useRef(0);

  // Debounce the query string. The dates are stable (calendar inputs, not
  // typeahead), so they don't need debouncing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const fetchOnce = useCallback(async () => {
    if (!enabled) return;
    const mySeq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (group) params.set("group", group);
      const url =
        "/api/dashboards/pullman/menu-card-bookings/search" +
        (params.toString() ? `?${params.toString()}` : "");
      const json = await getJson<MenuCardBookingsSearchResponse>(url);
      if (mySeq === seqRef.current) {
        setData(json);
      }
    } catch (err) {
      if (mySeq === seqRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mySeq === seqRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, debouncedQ, from, to, group]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { data, loading, error, refresh: fetchOnce };
}

// ---------------------------------------------------------------------------
// useTriggerMenuCardsRefresh()
//
// Two-phase:
//   1. POST /refresh-menu-cards — enqueues a row, returns the request id.
//   2. Poll GET /refresh-requests/{id} every POLL_INTERVAL_MS until
//      status === 'completed' | 'error', or POLL_TIMEOUT_MS elapses.
//
// The Sync Hub scheduler picks the request up on its next tick (about a
// minute at most) and the jobs take about 30 seconds. We poll every 5 seconds
// for a responsive UI.
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 10 * 60_000; // 10 minutes — plenty of margin.

export interface UseTriggerMenuCardsRefreshResult {
  trigger: () => Promise<void>;
  /** True from the moment the user clicks until the request finishes. */
  busy: boolean;
  /** Most recent request status (live-updated as we poll). */
  request: RefreshRequestStatusResponse | null;
  /** Last error from the POST or any poll cycle. Cleared on next trigger. */
  error: string | null;
  /** True after a 'completed' has been observed at least once this session. */
  succeeded: boolean;
}

export function useTriggerMenuCardsRefresh(
  onComplete?: (req: RefreshRequestStatusResponse) => void
): UseTriggerMenuCardsRefreshResult {
  const [busy, setBusy] = useState(false);
  const [request, setRequest] = useState<RefreshRequestStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  // We hold the latest onComplete in a ref so the trigger function below
  // doesn't need to rebuild on every render — keeps consumer code stable
  // even when they pass an inline arrow function.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const cancelRef = useRef(false);

  const trigger = useCallback(async () => {
    if (busy) return;
    cancelRef.current = false;
    setBusy(true);
    setError(null);
    setSucceeded(false);
    setRequest(null);

    let postResp: RefreshMenuCardsPostResponse;
    try {
      const res = await fetch(
        "/api/dashboards/pullman/refresh-menu-cards",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        }
      );
      if (!res.ok && res.status !== 202) {
        let detail = res.statusText;
        try {
          const b = await res.json();
          if (b?.error) detail = String(b.error);
        } catch {
          /* ignore */
        }
        throw new Error(`${res.status} ${detail}`);
      }
      postResp = (await res.json()) as RefreshMenuCardsPostResponse;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
      return;
    }

    // Initial state from the POST response — looks like a partial request row.
    setRequest({
      id: postResp.id,
      kind: "menu-cards",
      requested_at: postResp.requested_at,
      requested_by: postResp.requested_by,
      started_at: null,
      completed_at: null,
      status: postResp.status,
      message: postResp.already_in_flight
        ? "Picked up an in-flight refresh — joining its progress."
        : null,
      rows_total: null,
      duration_ms: null,
    });

    // 2) Poll until terminal.
    const startAt = Date.now();
    const id = postResp.id;
    const isTerminal = (s: RefreshRequestStatus) =>
      s === "completed" || s === "error";

    while (!cancelRef.current && Date.now() - startAt < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (cancelRef.current) break;
      try {
        const status = await getJson<RefreshRequestStatusResponse>(
          `/api/dashboards/pullman/refresh-requests/${id}`
        );
        setRequest(status);
        if (isTerminal(status.status)) {
          if (status.status === "completed") {
            setSucceeded(true);
            try {
              onCompleteRef.current?.(status);
            } catch (cbErr) {
              console.error("[useTriggerMenuCardsRefresh] onComplete threw:", cbErr);
            }
          } else {
            setError(status.message ?? "Refresh failed (no message returned).");
          }
          break;
        }
      } catch (err) {
        // Don't kill the poll on a single transient error — log and continue.
        console.warn("[useTriggerMenuCardsRefresh] poll error (continuing):", err);
      }
    }

    if (!cancelRef.current && Date.now() - startAt >= POLL_TIMEOUT_MS) {
      setError("Refresh is taking longer than expected. The job may still complete in the background.");
    }
    setBusy(false);
  }, [busy]);

  // Cancel any in-flight poll loop on unmount.
  useEffect(() => {
    return () => {
      cancelRef.current = true;
    };
  }, []);

  return useMemo(
    () => ({ trigger, busy, request, error, succeeded }),
    [trigger, busy, request, error, succeeded]
  );
}
